import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarCheck, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Panel, adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import { useBookingConfirmation, useSaveBookingConfirmation } from '@/hooks/useBookingConfirmation';
import {
  AUTO_ASSIGN_RULE,
  BOOKING_CONFIRMATION_MODES,
  DEFAULT_BOOKING_CONFIRMATION,
  MULTI_CLEANER_RULE,
  type BookingConfirmationSettings,
} from '@/lib/bookingConfirmation';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  mode: z.enum(BOOKING_CONFIRMATION_MODES),
  autoAssign: z.boolean(),
});
type FormValues = BookingConfirmationSettings;

const OPTIONS: Array<{ value: FormValues['mode']; title: string; body: string }> = [
  {
    value: 'instant',
    title: 'Instantly',
    body: 'Customers get confirmation straight away when the slot is free.',
  },
  {
    value: 'approval',
    title: 'I’ll approve each one',
    body: 'Bookings come in as requests. Nothing is confirmed until you say so.',
  },
];

/**
 * "How do bookings get confirmed?" — instant vs owner approval, plus optional
 * auto-assign. Lives on Operational Hours because instant confirmation leans on
 * the opening hours and team availability set there.
 */
export function BookingConfirmationPanel({ orgId, canWrite }: { orgId: string | null | undefined; canWrite: boolean }) {
  const { toast } = useToast();
  const { data, isLoading } = useBookingConfirmation(orgId);
  const save = useSaveBookingConfirmation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_BOOKING_CONFIRMATION,
  });
  const mode = form.watch('mode');
  const dirty = form.formState.isDirty;

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!orgId || !canWrite) return;
    try {
      const saved = await save.mutateAsync({ orgId, settings: { mode: values.mode, autoAssign: values.autoAssign } });
      form.reset(saved);
      toast({
        title: 'Saved',
        description: saved.mode === 'instant'
          ? 'New bookings will be confirmed straight away when the slot is free.'
          : 'New bookings will come to you to approve.',
      });
    } catch (error) {
      console.error('Could not save booking confirmation setting', error);
      toast({ title: 'Couldn’t save', description: 'Please try again.', variant: 'destructive' });
    }
  });

  return (
    <Panel
      title="How do bookings get confirmed?"
      icon={<CalendarCheck className="h-4 w-4 text-muted-foreground" />}
      description="Happy with your hours and your team’s availability? Let customers book in without waiting for you."
      data-testid="booking-confirmation-panel"
    >
      <form onSubmit={onSubmit} className="space-y-4" aria-busy={isLoading}>
        <Controller
          control={form.control}
          name="mode"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={(value) => field.onChange(value as FormValues['mode'])}
              disabled={!canWrite || isLoading}
              aria-label="How do bookings get confirmed?"
              className="grid gap-2"
            >
              {OPTIONS.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={`booking-confirm-${option.value}`}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-muted/50',
                    field.value === option.value && 'border-primary/40 bg-secondary/60',
                    !canWrite && 'cursor-not-allowed opacity-70',
                  )}
                >
                  <RadioGroupItem id={`booking-confirm-${option.value}`} value={option.value} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-semibold text-foreground">{option.title}</span>
                    <span className="mt-0.5 block text-[12.5px] font-normal leading-relaxed text-muted-foreground">{option.body}</span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          )}
        />

        {mode === 'instant' && (
          <div className="space-y-3 rounded-xl border border-dashed border-border p-3">
            <div className="flex items-start justify-between gap-3">
              <Label htmlFor="booking-auto-assign" className="text-[13.5px] font-semibold leading-snug text-foreground">
                Put a free cleaner on it for me
              </Label>
              <Controller
                control={form.control}
                name="autoAssign"
                render={({ field }) => (
                  <Switch
                    id="booking-auto-assign"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite || isLoading}
                  />
                )}
              />
            </div>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">{AUTO_ASSIGN_RULE}</p>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">{MULTI_CLEANER_RULE}</p>
          </div>
        )}

        <p className="text-[12px] leading-relaxed text-muted-foreground">
          {mode === 'instant'
            ? 'We check the slot again the moment they book. If anything’s changed, or it’s outside your hours, it comes to you as a request instead.'
            : 'You’ll find new requests at the top of your Schedule, ready to approve.'}
        </p>

        {dirty && (
          <div className="flex justify-end">
            <Button type="submit" size="sm" className={adminButton.primary} disabled={!canWrite || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
              Save
            </Button>
          </div>
        )}
      </form>
    </Panel>
  );
}
